import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { FeiraModel } from "../model/event.model";
import { EventService } from "../event.service";
import { NgbDropdownModule, NgbModal, NgbPaginationModule } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmationDialogService } from "src/app/shared/confirmation-dialog/confirmation-dialog.service";
import { EventFormComponent } from "../event-form/event-form.component";
import { SharedModule } from "src/app/shared/shared.module";
import { AuthService } from "src/app/core/services/auth/auth.service";
import { ErrorMessagesService } from "src/app/shared/messages/services/error-messages.service";
import { FiltrarFeira } from "../models/feira-filter.model";
import { PaginationDto } from "src/app/core/dto/pagination.dto";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ToastService } from "src/app/shared/toasts/toast-service";

@Component({
  selector: "app-event-list",
  standalone: true,
  imports: [NgbDropdownModule, SharedModule, ReactiveFormsModule, NgbPaginationModule],
  templateUrl: "./event-list.component.html",
  styleUrl: "./event-list.component.scss",
})
export class EventListComponent implements OnInit, OnDestroy {
  breadCrumbItems: Array<{}> = [];
  events: FeiraModel[] = [];
  isLoading: boolean = false;
  filtroForm!: FormGroup;
  private readonly destroy$ = new Subject<void>();
  private atualizandoPaginaProgramaticamente = false;

  error = "";
  erro403 = false;
  // permissoes
  podeVer: boolean = false;
  podeEditar: boolean = false;

  pagination: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 1
  };

  constructor(
    private eventService: EventService,
    private modalService: NgbModal,
    private confirmationDialogService: ConfirmationDialogService,
    private authService: AuthService,
    private errorMessagesService: ErrorMessagesService,
    private fb: FormBuilder,
    private toastService: ToastService
  ) {
    this.filtroForm = this.fb.group({
      nome: [''],
      paginaAtual: ['1'],
      quantidadePorPagina: ['10']
    });
  }
  openEventModal(event?: FeiraModel): void {
    const modalRef = this.modalService.open(EventFormComponent);
    if (event) {
      modalRef.componentInstance.event = event;
    }
    modalRef.closed.subscribe({
      next: () => {
        this.carregarFeiras();
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Call center" },
      { label: "Feiras", active: true },
    ];

    this.permissaoAdicionarUsuario();
    this.configurarFiltros();
    this.carregarFeiras();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  configurarFiltros(): void {
    // Debounce para o filtro de nome
    this.filtroForm.get('nome')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.filtroForm.patchValue({ paginaAtual: '1' }, { emitEvent: false });
        this.carregarFeiras();
      });
  }

  carregarFeiras(): void {
    const formValue = this.filtroForm.value;
    // API espera paginaAtual como 1-based (default é 1), mas retorna 0-based
    const paginaAtual = Math.max(1, parseInt(formValue.paginaAtual) || 1);
    
    const filtro: FiltrarFeira = {
      nome: formValue.nome || '',
      paginaAtual: paginaAtual.toString(), // Enviar 1-based (conforme default da API)
      quantidadePorPagina: formValue.quantidadePorPagina || '10'
    };

    this.getFeiras(filtro);
  }

  getFeiras(filtro: FiltrarFeira): void {
    this.isLoading = true;
    this.error = '';

    this.eventService.consultarFeira(filtro)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.events = response.feiras || [];
          
          // API retorna paginaAtual como 1-based (já está no formato correto para ngb-pagination)
          const novaPagina = Math.max(1, response.paginacao?.paginaAtual ?? 1);
          
          // Marcar que estamos atualizando programaticamente para evitar loop
          this.atualizandoPaginaProgramaticamente = true;
          
          // Atualizar paginação
          this.pagination = {
            paginaAtual: novaPagina,
            quantidadePorPagina: response.paginacao?.quantidadePorPagina || 10,
            totalDeItens: response.paginacao?.totalDeItens || 0,
            ultimaPagina: Math.max(1, response.paginacao?.ultimaPagina || 1)
          };
          
          // Sincronizar formulário sem disparar eventos
          if (this.filtroForm.get('paginaAtual')?.value !== novaPagina.toString()) {
            this.filtroForm.patchValue({ paginaAtual: novaPagina.toString() }, { emitEvent: false });
          }
          
          this.isLoading = false;
          
          // Resetar flag após um pequeno delay
          setTimeout(() => {
            this.atualizandoPaginaProgramaticamente = false;
          }, 100);
        },
        error: (error) => {
          if (error.status == 403) {
            this.erro403 = true;
            this.isLoading = false;
            return;
          }

          this.error = this.errorMessagesService.getMessage('ERR_0004');
          this.toastService.show('Erro ao consultar lista de feiras', {
            classname: 'bg-danger text-light',
          });
          this.isLoading = false;
        }
      });
  }

  onPageChange(page: number): void {
    if (!this.atualizandoPaginaProgramaticamente) {
      this.filtroForm.patchValue({ paginaAtual: page.toString() }, { emitEvent: false });
      this.carregarFeiras();
    }
  }

  limparFiltros(): void {
    this.filtroForm.patchValue({
      nome: '',
      paginaAtual: '1',
      quantidadePorPagina: '10'
    }, { emitEvent: false });
    this.carregarFeiras();
  }

  onDeleteStatus(event: FeiraModel) {
    this.confirmationDialogService
      .confirm(
        "Confirma a exclusão da feira ?",
        `Essa ação ira excluir a feira ${event.nome} e não poderá ser desfeita.`,
      )
      .then((confirmed) => {
        if (confirmed) {
          this.eventService.deleteEvent(event.id!).subscribe({
            next: () => {
              this.toastService.show('Feira excluída com sucesso', {
                classname: 'bg-success text-light',
              });
              this.carregarFeiras();
            },

            error: (error) => {
              this.error = this.errorMessagesService.getMessage('ERR_0005');
              this.toastService.show('Erro ao excluir feira', {
                classname: 'bg-danger text-light',
              });
              console.error(error);
            },
          });
        }
      });
  }

  permissaoAdicionarUsuario(): void {
    this.podeEditar = this.authService.verificarPermissao('Call_Feira', 'Editar')
    this.podeVer = this.authService.verificarPermissao('Call_Feira', 'Ver')
  }
}
